import { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';
import { formatQueryType } from '../../../utils/escalatedQueriesUtils';
import { DEFAULT_QUERY_TYPE_OPTIONS } from '../../../constants/escalatedQueriesPage';
import { buildProjectFilterOptionsFromApi } from '../../../utils/projectsUtils';

/**
 * Hook to manage query data fetching and processing for EscalatedQueries page
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query string
 * @param {Object} params.filters - Filter object
 * @param {Function} params.setAllQueries - Setter for all queries
 * @param {Function} params.setLoading - Setter for loading state
 * @param {Function} params.setProjectOptions - Setter for project options
 * @param {Function} params.setQueryTypeOptions - Setter for query type options
 * @param {Function} params.setStatusSummary - Setter for status summary
 * @param {Function} params.setEdUsersByRegion - Setter for ED users by region
 * @param {Function} params.setCurrentUser - Setter for current user
 * @returns {Function} Fetch queries function
 */
export const useEscalatedQueriesPageData = ({
  searchQuery,
  filters,
  setAllQueries,
  setLoading,
  setProjectOptions,
  setQueryTypeOptions,
  setStatusSummary,
  setBackendCounts,
  setEdUsersByRegion,
  setCurrentUser,
}) => {
  const { showSnackbar } = useSnackbar();
  const [projectsCatalog, setProjectsCatalog] = useState([]);

  // Fetch projects for filters
  useEffect(() => {
    const fetchProjectsForFilters = async () => {
      try {
        const response = await api.get('/projects', {
          params: {
            limit: 1000,
          },
        });
        const projectsData =
          response.data?.data?.projects ||
          response.data?.projects ||
          [];
        const list = Array.isArray(projectsData) ? projectsData : [];
        setProjectsCatalog(list);
        setProjectOptions(buildProjectFilterOptionsFromApi(list));
      } catch (error) {
        // console.error('Failed to load projects for filters:', error);
      }
    };

    fetchProjectsForFilters();
  }, [setProjectOptions]);

  // Fetch ED users by region
  useEffect(() => {
    const fetchEdUsers = async () => {
      try {
        const response = await api.get('/auth/ed-users-by-region');
        if (response.data?.success && response.data?.data?.edByRegion) {
          setEdUsersByRegion(response.data.data.edByRegion);
        }
      } catch (error) {
        // console.error('Failed to load ED users:', error);
      }
    };

    fetchEdUsers();
  }, [setEdUsersByRegion]);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        showSnackbar('User not found. Please login again.', 'error');
        setLoading(false);
        return;
      }

      const parsedUser = JSON.parse(userStr);
      setCurrentUser(parsedUser);

      const params = {
        page: 1,
        limit: 1000,
      };

      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (filters.projectName) params.projectName = filters.projectName;
      if (filters.queryType) params.queryType = filters.queryType;

      // Use notified queries endpoint to get queries where user is a higher-level assignee
      const response = await api.get('/queries/notified', { params });

      // ── Backend counts (single source of truth for stat cards) ──────────────
      const notifiedCounts = response.data?.counts?.notifiedQueries || null;
      if (setBackendCounts) setBackendCounts(notifiedCounts);
      // ────────────────────────────────────────────────────────────────────────

      let allQueries = response.data.data.queries || [];

      // Client-side fallback search
      const trimmedSearch = (searchQuery || '').trim().toLowerCase();
      if (trimmedSearch) {
        allQueries = allQueries.filter((q) => {
          const id = (q.queryId || q._id || '').toString().toLowerCase();
          const header = (q.queryHeader || '').toLowerCase();
          const desc = (q.queryDescription || '').toLowerCase();
          const project = (q.projectName || '').toLowerCase();
          return (
            id.includes(trimmedSearch) ||
            header.includes(trimmedSearch) ||
            desc.includes(trimmedSearch) ||
            project.includes(trimmedSearch)
          );
        });
      }

      // Backend already filtered queries where user is a higher-level assignee
      // Show all notified queries (no need to filter by status)
      let filteredQueries = allQueries;

      const dynamicQueryTypes = new Set(
        filteredQueries
          .map((query) => query?.queryType)
          .filter((type) => typeof type === 'string' && type.trim() !== '')
      );
      setQueryTypeOptions((prev) => {
        const merged = [...prev];
        dynamicQueryTypes.forEach((type) => {
          const formattedLabel = formatQueryType(type);
          const exists = merged.some(
            (option) => option.label.toLowerCase() === formattedLabel.toLowerCase()
          );
          if (!exists) {
            merged.push({
              value: formattedLabel,
              label: formattedLabel,
            });
          }
        });
        return merged
          .slice()
          .sort((a, b) =>
            (a.label || '').localeCompare(b.label || '', undefined, {
              sensitivity: 'base',
            })
          );
      });

      let processedQueries = [...filteredQueries];
      if (filters.projectName) {
        processedQueries = processedQueries.filter(
          (query) =>
            (query.projectName || '').toString().toLowerCase() ===
            filters.projectName.toString().toLowerCase()
        );
      }

      if (filters.queryType) {
        processedQueries = processedQueries.filter(
          (query) =>
            (query.queryType || '').toString().toLowerCase() ===
            filters.queryType.toString().toLowerCase()
        );
      }

      setAllQueries(processedQueries);
      // Status summary is now driven by backend counts via buildRegionStatusSummary.
      // The setStatusSummary call is intentionally omitted.
    } catch (error) {
      // console.error('Error fetching notified queries:', error);
      showSnackbar('Failed to load notified queries', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, setAllQueries, setLoading, setQueryTypeOptions, setStatusSummary, setBackendCounts, setCurrentUser, showSnackbar]);

  // Fetch queries when search or filters change
  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters]);

  return {
    fetchQueries,
    projectsCatalog,
  };
};
